/* AURTRS product page (sections/aurtrs-main-product.liquid).
 *
 * <at-gallery>  swipeable media track with thumbnail rail, arrows, dots and a
 *               zoom dialog. The element is replaced wholesale on variant change
 *               (product-info.js → updateMedia), so all wiring lives in
 *               connectedCallback and needs no teardown beyond observers.
 *
 * Variant change also refreshes the price inside the add-to-cart button
 * (#AtcPrice-<section>), which Dawn's product-info.js does not know about.
 */

if (!customElements.get('at-gallery')) {
  customElements.define(
    'at-gallery',
    class AtGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.at-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.at-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.at-gallery__thumb'));
        this.dots = Array.from(this.querySelectorAll('.at-gallery__dot'));
        this.dialog = this.querySelector('.at-gallery__zoom');
        this.index = 0;
        if (!this.track) return;

        this.querySelectorAll('.at-gallery__arrow').forEach((btn) =>
          btn.addEventListener('click', () => this.go(this.index + Number(btn.dataset.dir)))
        );
        this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.go(Number(btn.dataset.index))));
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.go(this.index + 1);
          if (e.key === 'ArrowLeft') this.go(this.index - 1);
        });

        // The slide that is mostly in view is the active one (covers swipes too).
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));

        this.querySelector('.at-gallery__zoom-open')?.addEventListener('click', () => this.openZoom());
        this.querySelector('.at-gallery__zoom-close')?.addEventListener('click', () => this.dialog.close());
        this.dialog?.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.dialog.close();
        });

        this.playActiveVideo();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      go(index) {
        const count = this.slides.length;
        if (!count) return;
        const next = (index + count) % count;
        this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: 'smooth' });
        this.setActive(next);
      }

      setActive(index) {
        if (index < 0 || index === this.index) return;
        this.index = index;
        this.thumbs.forEach((t, i) => {
          t.classList.toggle('is-active', i === index);
          if (i === index) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
        this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.playActiveVideo();
      }

      playActiveVideo() {
        this.slides.forEach((slide, i) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (i === this.index) video.play().catch(() => {});
          else video.pause();
        });
      }

      openZoom() {
        if (!this.dialog) return;
        this.dialog.showModal();
        const slide = this.slides[this.index];
        const img = slide && this.dialog.querySelector(`img[data-index="${slide.dataset.index}"]`);
        if (img) img.scrollIntoView({ block: 'start' });
      }
    }
  );
}

(() => {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const id = `AtcPrice-${data.sectionId}`;
    const source = data.html.getElementById(id);
    const target = document.getElementById(id);
    if (source && target) target.innerHTML = source.innerHTML;
  });
})();

/* Size / weight guide dialog (snippets/aurtrs-size-guide.liquid). The trigger sits inside
   <variant-selects>, which is re-rendered on every variant change, so clicks are delegated. */
(() => {
  const parseSize = (text) => {
    const m = /(\d+(?:\.\d+)?)\D+x\D*(\d+(?:\.\d+)?)/i.exec(text || '');
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  };

  const setWeight = (dialog, button) => {
    const size = parseSize(button.dataset.detail);
    dialog.querySelectorAll('[data-at-guide-weight]').forEach((b) => b.classList.toggle('is-active', b === button));
    const beds = dialog.querySelector('[data-at-guide-beds]');
    if (beds && size) {
      beds.style.setProperty('--kw', size.w);
      beds.style.setProperty('--kh', size.h);
    }
    const dims = dialog.querySelector('[data-at-guide-dims]');
    if (dims && size) dims.textContent = `${button.dataset.label} blanket: ${size.w} x ${size.h} in`;
  };

  const optionInput = (dialog, value) => {
    const scope = dialog.closest('product-info') || document;
    return Array.from(scope.querySelectorAll('variant-selects input[type="radio"]')).find(
      (input) => input.name.startsWith(dialog.dataset.optionName) && input.value === value
    );
  };

  const open = (dialog) => {
    const scope = dialog.closest('product-info') || document;
    const buttons = Array.from(dialog.querySelectorAll('[data-at-guide-weight]'));
    const checked = Array.from(scope.querySelectorAll('variant-selects input[type="radio"]:checked')).find((input) =>
      input.name.startsWith(dialog.dataset.optionName)
    );
    const current = buttons.find((b) => checked && b.dataset.atGuideWeight === checked.value) || buttons[0];
    if (current) setWeight(dialog, current);
    dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-at-guide-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.atGuideOpen);
      if (dialog) open(dialog);
      return;
    }

    const dialog = event.target.closest('[data-at-guide]');
    if (!dialog) return;
    if (event.target === dialog || event.target.closest('[data-at-guide-close]')) return dialog.close();

    const tab = event.target.closest('[data-at-guide-tab]');
    if (tab) return void (dialog.dataset.tab = tab.dataset.atGuideTab);

    const weight = event.target.closest('[data-at-guide-weight]');
    if (weight) return setWeight(dialog, weight);

    const panel = dialog.querySelector('[data-at-guide-panel="weight"]');
    if (event.target.closest('[data-at-guide-back]')) return void (panel.dataset.result = 'false');

    if (event.target.closest('[data-at-guide-choose]')) {
      const input = optionInput(dialog, panel.dataset.value);
      if (input && !input.checked) input.click();
      dialog.close();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-at-guide-form]');
    if (!form) return;
    event.preventDefault();
    const dialog = form.closest('[data-at-guide]');
    const panel = form.closest('[data-at-guide-panel]');
    const field = form.elements.body_weight;
    const body = parseFloat(field.value);
    field.classList.toggle('is-invalid', !(body > 0));
    if (!(body > 0)) return;

    // 10% of body weight, rounded down to a weight this blanket comes in
    const options = JSON.parse(dialog.querySelector('[data-at-guide-options]').textContent)
      .map((o) => ({ ...o, lbs: parseFloat(o.value) }))
      .filter((o) => o.lbs > 0)
      .sort((a, b) => a.lbs - b.lbs);
    if (!options.length) return;
    const pick = options.filter((o) => o.lbs <= body / 10).pop() || options[0];
    panel.dataset.value = pick.value;
    panel.querySelector('[data-at-guide-result]').textContent = `${pick.label} ${dialog.dataset.productTitle}`;
    const size = parseSize(pick.detail);
    panel.querySelector('[data-at-guide-result-size]').textContent = size ? `${size.w} x ${size.h} in` : '';
    panel.querySelector('[data-at-guide-choose]').textContent = `Choose ${pick.label}`;
    panel.dataset.result = 'true';
  });
})();
